import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Ticket,
  Users,
  Clock,
  AlertTriangle,
  X,
  CheckCircle,
  Calendar,
  Flag,
} from "lucide-react";
import Swal from "sweetalert2";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import { API_URL } from "../components/config";

const AllTicket = () => {
  const [tickets, setTickets] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [selectedAttendant, setSelectedAttendant] = useState({});
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({
    conversation_stage: "",
    person_assigned: "",
    expiry_date_time: "",
    priority_level: "",
    estimation_duration: "",
  });
  const [filterStage, setFilterStage] = useState(""); // New state for filter

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [ticketsResponse, attendantsResponse] = await Promise.all([
        axios.get(`${API_URL}/tickets/`),
        axios.get(`${API_URL}/users`),
      ]);
      setTickets(ticketsResponse.data);
      setAttendants(attendantsResponse.data);
      console.log("attendants", attendantsResponse.data);
    } catch (err) {
      showToast("error", "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const showToast = (icon, title) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: "#002966",
      color: "#ffffff",
    });
  };

  const handleAllocateTicket = async (ticketId) => {
    try {
      setIsLoading(true);
      await axios.put(`${API_URL}/tickets/${ticketId}`, {
        person_assigned: selectedAttendant[ticketId],
      });
      showToast("success", "Ticket allocated successfully!");
      setSelectedAttendant((prev) => ({ ...prev, [ticketId]: "" }));
      fetchData();
    } catch (err) {
      showToast("error", "Failed to allocate ticket");
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (ticket) => {
    setSelectedTicket(ticket);
    setUpdatedFields({
      department: ticket.department,
      description: ticket.description,
      chat_id: ticket.chat_id,
      product: ticket.product,
      company: ticket.company,
      surname: ticket.surname,
      email: ticket.email,
      phone2: ticket.phone2,
      phone1: ticket.phone1,
      customer_name: ticket.customer_name,
      customer_id: ticket.customer_id,
      conversation_stage: "assigned",
      created_at: ticket.created_at,
      person_assigned: ticket.person_assigned,
      expiry_date_time: ticket.expiry_date_time,
      priority_level: ticket.priority_level,
      estimation_duration: ticket.estimation_duration,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  const handleUpdateTicket = async () => {
    try {
      setIsLoading(true);
      await axios.put(
        `${API_URL}/tickets/${selectedTicket.conversation_support_id}`,
        {
          ...updatedFields,
        }
      );
      showToast("success", "Ticket updated successfully!");
      closeModal();
      fetchData();
    } catch (err) {
      showToast("error", "Failed to update ticket");
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (level) => {
    const levels = {
      1: "bg-green-100 text-green-800",
      2: "bg-yellow-100 text-yellow-800",
      3: "bg-red-100 text-red-800",
    };
    return levels[level] || "bg-gray-100 text-gray-800";
  };

  // Filter tickets based on the selected conversation stage
  const filteredTickets = filterStage
    ? tickets.filter((ticket) => ticket.conversation_stage === filterStage)
    : tickets;

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Ticket className="h-8 w-8 text-[#002966]" />
            <h1 className="text-3xl font-bold text-[#002966]">
              Ticket Allocation
            </h1>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchData}
            className="flex items-center space-x-2 rounded-lg bg-[#002966] px-4 py-2 text-white transition-all hover:bg-[#001a4d]"
          >
            <Clock className="h-5 w-5" />
            <span>Refresh Tickets</span>
          </motion.button>
        </div>

        {/* Filter Dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Conversation Stage
          </label>
          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-[#002966] focus:outline-none"
          >
            <option value="">All Stages</option>
            <option value="assigned">Assigned</option>
            <option value="Escalated">Escalated</option>
            <option value="Closed">Closed</option>
            {/* Add other stages as needed */}
          </select>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTickets.map((ticket) => (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={ticket.conversation_support_id}
              className="overflow-hidden rounded-xl bg-white p-6 shadow-lg transition-all hover:shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#002966]">
                  Ticket #{ticket.conversation_support_id}
                </h2>
                <span
                  className={`rounded-full px-3 py-1 text-sm ${getPriorityColor(
                    ticket.priority_level
                  )}`}
                >
                  Priority {ticket.priority_level}
                </span>
              </div>

              <div className="space-y-3">
                <p className="text-gray-600">{ticket.description}</p>
                <div className="flex items-center space-x-2">
                  <Flag className="h-4 w-4 text-[#ffc000]" />
                  <span className="font-medium text-gray-600">Status:</span>
                  <span className="text-[#002966]">
                    {ticket.conversation_stage}
                  </span>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => openModal(ticket)}
                  className="mt-4 w-full rounded-lg bg-[#ffc000] py-2 text-[#002966] font-semibold transition-all hover:bg-[#e6ac00]"
                >
                  Allocate Ticket
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-[#002966]">
                    Push Ticket
                  </h3>
                  <button
                    onClick={closeModal}
                    className="rounded-full p-1 hover:bg-gray-100"
                  >
                    <X className="h-6 w-6 text-gray-500" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Conversation Stage
                    </label>
                    <input
                      type="text"
                      value={updatedFields.conversation_stage}
                      onChange={(e) =>
                        setUpdatedFields({
                          ...updatedFields,
                          conversation_stage: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-[#002966] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Assign To
                    </label>
                    <select
                      value={updatedFields.person_assigned || ""}
                      onChange={(e) =>
                        setUpdatedFields({
                          ...updatedFields,
                          person_assigned: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-[#002966] focus:outline-none"
                    >
                      <option value="">Select Attendant</option>
                      {attendants.map((attendant) => (
                        <option key={attendant.userid} value={attendant.userid}>
                          {attendant.username} {attendant.userid}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Expiry Date Time
                    </label>
                    <input
                      type="datetime-local"
                      value={updatedFields.expiry_date_time}
                      onChange={(e) =>
                        setUpdatedFields({
                          ...updatedFields,
                          expiry_date_time: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-[#002966] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Priority Level
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="3"
                      value={updatedFields.priority_level}
                      onChange={(e) =>
                        setUpdatedFields({
                          ...updatedFields,
                          priority_level: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-[#002966] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Estimation Duration
                    </label>
                    <input
                      type="text"
                      value={updatedFields.estimation_duration}
                      onChange={(e) =>
                        setUpdatedFields({
                          ...updatedFields,
                          estimation_duration: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-[#002966] focus:outline-none"
                    />
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleUpdateTicket}
                      disabled={isLoading}
                      className="flex-1 rounded-lg bg-[#002966] py-2 text-white transition-all hover:bg-[#001a4d] disabled:opacity-50"
                    >
                      {isLoading ? "Updating..." : "Save Changes"}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={closeModal}
                      className="flex-1 rounded-lg bg-gray-200 py-2 text-gray-800 transition-all hover:bg-gray-300"
                    >
                      Cancel
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default AllTicket;
